/**
 * Resolves an Enonic service URL against the site or preview context of the current page.
 *
 * Enonic service URLs such as `_/service/<app>/<service>` need the current site and branch
 * in front of them. This keeps requests made from preview pages in preview and requests from
 * published pages on the matching site branch. Fully qualified URLs and non-service values
 * are returned unchanged.
 *
 * Preview paths use the admin preview route; normal site paths use the current site and branch;
 * other routes fall back to the nearest `master` or `draft` segment, or the root.
 *
 * @param serviceUrl - The raw service URL supplied by the server or page markup.
 * @param pathname - The current browser pathname used to find the site or preview context.
 * @returns The context-aware service URL, or the original value when it is not an Enonic service path.
 */
export function resolveServiceUrl(serviceUrl: string, pathname: string): string {
  if (!serviceUrl) {
    return serviceUrl;
  }

  // Absolute endpoints already identify their host and must not inherit the current site path.
  if (serviceUrl.startsWith("http://") || serviceUrl.startsWith("https://")) {
    return serviceUrl;
  }

  // Accept both a full Enonic service path and the shorthand used in page attributes.
  const marker = "/_/service/";
  const markerIndex = serviceUrl.indexOf(marker);
  let serviceSuffix: string | null = null;

  if (markerIndex !== -1) {
    serviceSuffix = serviceUrl.substring(markerIndex + 1);
  } else if (serviceUrl.startsWith("_/service/")) {
    serviceSuffix = serviceUrl;
  }

  if (serviceSuffix) {
    const pathParts = pathname.split("/").filter(Boolean);

    // Admin previews encode the site app and branch before the content path.
    if (pathParts[0] === "admin" && pathParts[1] === "site" && pathParts[2] === "preview" && pathParts[3] && pathParts[4]) {
      return `/admin/site/preview/${pathParts[3]}/${pathParts[4]}/${serviceSuffix}`;
    }

    // Published site URLs expose both values in their leading path segments.
    if (pathParts[0] === "site" && pathParts[1] && pathParts[2]) {
      return `/site/${pathParts[1]}/${pathParts[2]}/${serviceSuffix}`;
    }

    // Preserve branch context on other routes that include a master or draft segment.
    const contextIndex = pathParts.findIndex((part) => part === "master" || part === "draft");

    if (contextIndex >= 0) {
      const contextPath = `/${pathParts.slice(0, contextIndex + 1).join("/")}`;
      return `${contextPath}/${serviceSuffix}`;
    }

    // Custom routes without a detectable site or branch context use the root service path.
    return `/${serviceSuffix}`;
  }

  return serviceUrl;
}
